import * as TabsPrimitive from "@radix-ui/react-tabs";
import { motion, useReducedMotion } from "motion/react";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
  type ComponentPropsWithoutRef,
} from "react";

import { focusRing } from "../../foundations/a11y";
import { cn } from "../../lib/cn";
import { motion as motionTokens } from "../../tokens";

type TabsContextValue = {
  /** Currently active value — mirrors Radix so triggers can place the indicator. */
  active: string | undefined;
  /** Shared layoutId so the underline glides between triggers (unique per Tabs). */
  layoutId: string;
  reduce: boolean | null;
};

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error("Tabs subcomponents must be used within <Tabs>.");
  }
  return ctx;
}

export function Tabs({
  value,
  defaultValue,
  onValueChange,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Root>) {
  const reduce = useReducedMotion();
  const layoutId = useId();
  // Mirror Radix's value purely to position the decorative indicator; Radix
  // keeps owning selection (and its controlled/uncontrolled mode is untouched).
  const [active, setActive] = useState<string | undefined>(
    value ?? defaultValue,
  );
  useEffect(() => {
    if (value !== undefined) setActive(value);
  }, [value]);

  return (
    <TabsContext.Provider value={{ active, layoutId, reduce }}>
      <TabsPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => {
          setActive(next);
          onValueChange?.(next);
        }}
        {...rest}
      />
    </TabsContext.Provider>
  );
}

export function TabsList({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn("border-separator flex gap-1 border-b", className)}
      {...rest}
    />
  );
}

export function TabsTrigger({
  className,
  children,
  value,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>) {
  const { active, layoutId, reduce } = useTabsContext();
  const selected = active === value;

  return (
    <TabsPrimitive.Trigger
      value={value}
      className={cn(
        "text-label text-text-secondary hover:text-text relative px-3 py-2 font-medium transition-colors duration-fast ease-standard",
        "data-[state=active]:text-text",
        focusRing,
        className,
      )}
      {...rest}
    >
      {children}
      {selected &&
        (reduce ? (
          <span
            aria-hidden="true"
            className="bg-accent absolute inset-x-0 -bottom-px h-0.5"
          />
        ) : (
          <motion.span
            aria-hidden="true"
            layoutId={layoutId}
            className="bg-accent absolute inset-x-0 -bottom-px h-0.5"
            transition={motionTokens.spring.default}
          />
        ))}
    </TabsPrimitive.Trigger>
  );
}

export function TabsContent({
  className,
  ...rest
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      className={cn("pt-4 focus:outline-none", className)}
      {...rest}
    />
  );
}
